import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Field,
  Spinner,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled } from '@fluentui/react-icons';
import React, { useState } from 'react';
import {
  ContainerType,
  ContainerPermissions,
  CreateContainerDto,
} from './types/NewContainerTypes';
import { useStyles } from './NewContainerDialog.styles';

export interface ValidationErrors {
  [key: string]: string[];
}

interface PermissionInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id: string;
  className: string;
  validationError?: string;
}

const PermissionInput: React.FC<PermissionInputProps> = ({
  label,
  value,
  onChange,
  id,
  className,
  validationError,
}) => (
  <Field
    label={label}
    validationState={validationError ? 'error' : 'none'}
    validationMessage={validationError}
  >
    <Input
      className={className}
      placeholder="email1@example.com, email2@example.com"
      id={id}
      aria-label={id}
      type="text"
      name={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

export interface INewContainerDialogProps {
  showNewContainerDialog: boolean;
  showCreatingSpinner: boolean;
  validationErrors?: ValidationErrors;
  createdSuccessfully?: boolean;
  onItemsUpdated: () => void;
  createNewContainer: (createContainerDto: CreateContainerDto) => void;
  closeNewContainerModal: () => void;
}

export const NewContainerDialog: React.FunctionComponent<
  INewContainerDialogProps
> = (props: INewContainerDialogProps) => {
  const styles = useStyles();
  const [containerName, setContainerName] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [containerType, setContainerType] = useState<ContainerType>(
    ContainerType.General,
  );
  const [managers, setManagers] = useState<string>('');
  const [writers, setWriters] = useState<string>('');
  const [readers, setReaders] = useState<string>('');
  const [servicePrincipals, setServicePrincipals] = useState<string>('');
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  // Helper to get validation error for permission fields (handles both direct and array-indexed keys)

  // Helper to get validation error for permission fields (handles both direct and array-indexed keys)
  const getPermissionError = (
    validationErrors: ValidationErrors | undefined,
    fieldPrefix: string,
  ): string | undefined => {
    if (!validationErrors) return undefined;
    if (validationErrors[fieldPrefix]) {
      return validationErrors[fieldPrefix][0];
    }
    const arrayKey = Object.keys(validationErrors).find((key) =>
      key.startsWith(`${fieldPrefix}[`),
    );
    return arrayKey ? validationErrors[arrayKey][0] : undefined;
  };

  // Helper to check if at least one permission is provided
  const isAnyPermissionProvided = (
    managers: string,
    writers: string,
    readers: string,
  ): boolean => {
    return (
      managers.trim() !== '' || writers.trim() !== '' || readers.trim() !== ''
    );
  };

  // Helper to get validation state and message for a field
  const getFieldValidation = (
    touched: boolean | undefined,
    value: string,
    validationError?: string,
    requiredMessage?: string,
  ) => {
    if (validationError) {
      return { state: 'error' as const, message: validationError };
    }
    if (touched && value.trim() === '') {
      return { state: 'error' as const, message: requiredMessage };
    }
    return { state: 'none' as const, message: undefined };
  };

  // Validation states
  const containerNameValidation = getFieldValidation(
    touched.containerName,
    containerName,
    props.validationErrors?.name?.[0],
    'Container name is required',
  );
  const clientIdValidation = getFieldValidation(
    touched.clientId,
    clientId,
    undefined,
    'Client ID is required',
  );
  const isPermissionProvided = isAnyPermissionProvided(
    managers,
    writers,
    readers,
  );
  const permissionsValidation = getFieldValidation(
    touched.permissions,
    isPermissionProvided ? 'x' : '',
    props.validationErrors?.permissions?.[0],
    'At least one permission (Managers, Writers, or Readers) must be provided',
  );

  // Service Principals validation (required if Creative)
  const servicePrincipalsValidation =
    containerType === ContainerType.Creative
      ? getFieldValidation(
          touched.servicePrincipals,
          servicePrincipals,
          props.validationErrors?.service_principals?.[0],
          'Service Principals are required for Creative containers',
        )
      : { state: 'none' as const, message: undefined };

  const handlePermissionChange = (
    value: string,
    setter: (value: string) => void,
  ) => {
    setter(value);
    if (value.trim() !== '') {
      setTouched((prev) => ({ ...prev, permissions: true }));
    }
  };

  const handleCreate = () => {
    // Mark all fields as touched
    setTouched((prev) => ({
      ...prev,
      containerName: true,
      clientId: true,
      permissions: true,
      ...(containerType === ContainerType.Creative
        ? { servicePrincipals: true }
        : {}),
    }));

    // Validate before submitting
    if (
      containerName.trim() === '' ||
      clientId.trim() === '' ||
      !isPermissionProvided ||
      (containerType === ContainerType.Creative &&
        servicePrincipals.trim() === '')
    ) {
      return;
    }

    const permissions: ContainerPermissions = {
      managers: managers
        ? managers.split(',').map((s) => s.trim()).filter((s) => s)
        : undefined,
      writers: writers
        ? writers.split(',').map((s) => s.trim()).filter((s) => s)
        : undefined,
      readers: readers
        ? readers.split(',').map((s) => s.trim()).filter((s) => s)
        : undefined,
    };

    // Prepare servicePrincipals only if Creative
    let servicePrincipalsArr: string[] | undefined = undefined;
    if (
      containerType === ContainerType.Creative &&
      servicePrincipals.trim() !== ''
    ) {
      servicePrincipalsArr = servicePrincipals
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s);
    }

    props.createNewContainer({
      name: containerName,
      client_id: Number(clientId),
      client_name: clientName,
      container_type: ContainerType[containerType],
      service_principals: servicePrincipalsArr || [],
      permissions: permissions,
    });
  };

  return (
    <Dialog open={props.showNewContainerDialog}>
      <DialogSurface>
        {props.createdSuccessfully && (
          <DialogBody className="kt-ui-app dialog-content">
            <div className={styles.successContainer}>
              <CheckmarkCircle24Filled className={styles.successIcon} />
              <div className={styles.successMessage}>
                Container created successfully!
              </div>
            </div>
          </DialogBody>
        )}
        {!props.showCreatingSpinner && !props.createdSuccessfully && (
          <DialogBody className="kt-ui-app dialog-content">
            <DialogTitle>New Container</DialogTitle>
            <DialogContent>
              <div className={styles.formContainer}>
                <Field
                  label="Container name"
                  required
                  validationState={containerNameValidation.state}
                  validationMessage={containerNameValidation.message}
                >
                  <Input
                    className={styles.fullWidthInput}
                    placeholder="Enter container name"
                    id="containerName"
                    aria-label="containerName"
                    type="text"
                    name="containerName"
                    value={containerName}
                    onChange={(e) => setContainerName(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, containerName: true }))
                    }
                  />
                </Field>
                <Field
                  label="Client ID"
                  required
                  validationState={clientIdValidation.state}
                  validationMessage={clientIdValidation.message}
                >
                  <Input
                    className={styles.fullWidthInput}
                    placeholder="Enter client ID"
                    id="clientId"
                    aria-label="clientId"
                    type="text"
                    name="clientId"
                    onChange={(e) => setClientId(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, clientId: true }))
                    }
                  />
                </Field>
                <Field label="Client Name">
                  <Input
                    className={styles.fullWidthInput}
                    placeholder="Enter client name (optional)"
                    id="clientName"
                    aria-label="clientName"
                    type="text"
                    name="clientName"
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </Field>
                <Field label="Container type">
                  <select
                    title="Container type"
                    id="containerType"
                    value={containerType}
                    onChange={(e) =>
                      setContainerType(Number(e.target.value) as ContainerType)
                    }
                    className={styles.selectInput}
                  >
                    <option value={ContainerType.General}>General</option>
                    <option value={ContainerType.MyKantar}>MyKantar</option>
                    <option value={ContainerType.Creative}>Creative</option>
                  </select>
                </Field>
                {/* ServicePrincipals field for Creative type */}
                {containerType === ContainerType.Creative && (
                  <Field
                    label="Service Principals (comma-separated)"
                    required
                    validationState={servicePrincipalsValidation.state}
                    validationMessage={servicePrincipalsValidation.message}
                  >
                    <Input
                      className={styles.fullWidthInput}
                      placeholder="principal1, principal2"
                      id="servicePrincipals"
                      aria-label="servicePrincipals"
                      type="text"
                      name="servicePrincipals"
                      value={servicePrincipals}
                      onChange={(e) => setServicePrincipals(e.target.value)}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          servicePrincipals: true,
                        }))
                      }
                    />
                  </Field>
                )}
                <Field
                  label="Permissions"
                  required
                  validationState={permissionsValidation.state}
                  validationMessage={permissionsValidation.message}
                >
                  <div
                    className={`${styles.permissionsGroup} ${permissionsValidation.state === 'error' ? styles.permissionsGroupError : ''}`}
                  >
                    <PermissionInput
                      label="Managers (comma-separated emails)"
                      value={managers}
                      onChange={(value) =>
                        handlePermissionChange(value, setManagers)
                      }
                      id="managers"
                      className={styles.fullWidthInput}
                        validationError={getPermissionError(
                        props.validationErrors,
                        'permissions.managers',
                      )}
                    />
                    <PermissionInput
                      label="Writers (comma-separated emails)"
                      value={writers}
                      onChange={(value) =>
                        handlePermissionChange(value, setWriters)
                      }
                      id="writers"
                      className={styles.fullWidthInput}
                        validationError={getPermissionError(
                        props.validationErrors,
                        'permissions.writers',
                      )}
                    />
                    <PermissionInput
                      label="Readers (comma-separated emails)"
                      value={readers}
                      onChange={(value) =>
                        handlePermissionChange(value, setReaders)
                      }
                      id="readers"
                      className={styles.fullWidthInput}
                        validationError={getPermissionError(
                        props.validationErrors,
                        'permissions.readers',
                      )}
                    />
                  </div>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleCreate}>
                Create
              </Button>
              <Button
                appearance="secondary"
                onClick={() => props.closeNewContainerModal()}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        )}
        {props.showCreatingSpinner && (
          <>
            <Spinner label="Creating container..." />
          </>
        )}
      </DialogSurface>
    </Dialog>
  );
};
